/**
 * Serialize in-memory chat Messages into OpenAI-shaped history for /api/chat-stream.
 * Includes recent tool rows so Continue / follow-up turns keep tool context (agent-context-depth P0).
 */
import type { Message } from '@/components/layout/chat-pane.types'

export type StreamChatMessage = {
  role: 'user' | 'assistant' | 'tool'
  content: string
  attachmentIds?: string[]
  images?: Array<{ mediaType: string; base64: string }>
  tool_calls?: Array<{
    id: string
    type: 'function'
    function: { name: string; arguments: string }
  }>
  tool_call_id?: string
  name?: string
}

export type ToolTraceEntry = {
  id: string
  name: string
  args?: unknown
  result?: unknown
}

/** Max JSON chars per tool result in the stream request (keeps payload under budget). */
export const MAX_TOOL_RESULT_CHARS = 12_000

/** How many trailing user turns keep tool rows on a normal (non-Continue) send. */
export const RECENT_USER_TURNS_WITH_TOOLS = 2

export function truncateToolPayload(value: unknown, maxChars = MAX_TOOL_RESULT_CHARS): string {
  let raw: string
  try {
    raw = typeof value === 'string' ? value : JSON.stringify(value ?? {})
  } catch {
    raw = String(value ?? '')
  }
  if (raw.length <= maxChars) return raw
  return `${raw.slice(0, maxChars)}\n[...truncated for context window; re-fetch or readToolResult if needed]`
}

function toolZoneStartIndex(
  messages: Message[],
  recentUserTurnsWithTools: number,
  agenticContinue: boolean,
): number {
  if (agenticContinue) {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return i
    }
    return 0
  }
  let usersSeen = 0
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      usersSeen += 1
      if (usersSeen >= recentUserTurnsWithTools) return i
    }
  }
  return 0
}

function resolveToolCallId(msg: Message, fallbackIndex: number): string {
  const fromMsg =
    (typeof msg.toolCallId === 'string' && msg.toolCallId) ||
    (typeof msg.metadata?.toolCallId === 'string' && (msg.metadata.toolCallId as string)) ||
    null
  return fromMsg || `tool_replay_${fallbackIndex}`
}

function resolveToolArgs(msg: Message, assistant: Message | undefined, toolIndexInBatch: number): unknown {
  if (msg.toolArgs != null) return msg.toolArgs
  const fromResults = assistant?.toolResults
  if (Array.isArray(fromResults) && fromResults.length > 0) {
    const byName = fromResults.filter((r) => r.tool === msg.toolName)
    if (byName[toolIndexInBatch]) return byName[toolIndexInBatch].args
    const match = fromResults.find((r) => r.tool === msg.toolName)
    if (match) return match.args
  }
  return {}
}

function collectAttachmentIds(msg: Message): string[] {
  const fromImages = (msg.images || [])
    .map((img) => img.attachmentId)
    .filter((id): id is string => Boolean(id))
  const fromMeta = Array.isArray(msg.metadata?.attachmentIds)
    ? msg.metadata.attachmentIds.map((id) => String(id || '').trim()).filter(Boolean)
    : []
  return [...new Set([...fromImages, ...fromMeta])]
}

/** Status of the diff card for `documentId` in the same user turn as `index`, if one is in memory. */
function proposedEditCardStatus(
  messages: Message[],
  index: number,
  documentId: string,
): 'pending' | 'accepted' | 'rejected' | null {
  let start = Math.min(index, messages.length - 1)
  while (start > 0 && messages[start - 1].role !== 'user') start -= 1
  let status: 'pending' | 'accepted' | 'rejected' | null = null
  for (let k = start; k < messages.length; k++) {
    if (k > index && messages[k].role === 'user') break
    const pe = messages[k].proposedEdit
    if (pe && String(pe.documentId) === documentId) status = pe.status
  }
  return status
}

/**
 * Replayed proposed_edit results carry the pending-at-the-time payload. Rewrite them with the
 * user's Accept/Reject outcome (or "unknown" after reload, when diff cards are not persisted).
 */
export function resolveProposedEditResult(
  messages: Message[],
  index: number,
  result: unknown,
): unknown {
  if (!result || typeof result !== 'object') return result
  const r = result as Record<string, unknown>
  if (r.type !== 'proposed_edit' || r.documentId == null) return result
  const status = proposedEditCardStatus(messages, index, String(r.documentId))
  if (status === 'pending') return result
  if (status === 'accepted') {
    return {
      ...r,
      status: 'accepted_by_user',
      applied: true,
      requires_acceptance: false,
      message: 'The user accepted this proposed edit. It is applied to the document.',
    }
  }
  if (status === 'rejected') {
    return {
      ...r,
      status: 'rejected_by_user',
      applied: false,
      requires_acceptance: false,
      message: 'The user rejected this proposed edit. The document was not changed by it.',
    }
  }
  return {
    ...r,
    status: 'proposed_outcome_unknown',
    requires_acceptance: false,
    message:
      'This edit was proposed for review (it did not fail). Whether the user accepted it is not ' +
      'recorded here: read the current document before saying it was or was not applied.',
  }
}

function lastUserMessageIndex(messages: Message[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return i
  }
  return -1
}

function emitUserAssistantOnly(
  msg: Message,
  options?: { sendImageBytes?: boolean },
): StreamChatMessage | null {
  if (msg.role !== 'user' && msg.role !== 'assistant') return null
  const out: StreamChatMessage = {
    role: msg.role,
    content: msg.content || '',
  }
  if (msg.role === 'user') {
    const attachmentIds = collectAttachmentIds(msg)
    if (attachmentIds.length) out.attachmentIds = attachmentIds
    const sendBytes =
      Boolean(options?.sendImageBytes) &&
      attachmentIds.length === 0 &&
      Boolean(msg.images?.length)
    if (sendBytes && msg.images?.length) {
      out.images = msg.images.map((img) => ({
        mediaType: img.mediaType,
        base64: img.base64,
      }))
    }
  }
  return out
}

/**
 * Build stream history. Tool rows outside the recent zone are dropped (user/assistant text kept).
 * Tool batches are emitted as assistant.tool_calls + role=tool pairs (required by Anthropic convert).
 */
export function serializeChatHistoryForStream(
  messages: Message[],
  options?: {
    agenticContinue?: boolean
    recentUserTurnsWithTools?: number
  },
): StreamChatMessage[] {
  const agenticContinue = Boolean(options?.agenticContinue)
  const recentUserTurnsWithTools = options?.recentUserTurnsWithTools ?? RECENT_USER_TURNS_WITH_TOOLS
  const zoneStart = toolZoneStartIndex(messages, recentUserTurnsWithTools, agenticContinue)
  const lastUserIdx = lastUserMessageIndex(messages)
  const out: StreamChatMessage[] = []
  let replayId = 0

  let i = 0
  while (i < messages.length) {
    const msg = messages[i]

    if (msg.role === 'tool') {
      if (i < zoneStart) {
        i += 1
        continue
      }
      // Orphan tool batch (no preceding assistant in this walk) - synthesize assistant tool_calls.
      const batchStart = i
      const batch: Message[] = []
      while (i < messages.length && messages[i].role === 'tool') {
        batch.push(messages[i])
        i += 1
      }
      const toolCalls = batch.map((t, batchIdx) => {
        const id = resolveToolCallId(t, replayId++)
        const args = resolveToolArgs(t, undefined, batchIdx)
        return {
          id,
          type: 'function' as const,
          function: {
            name: t.toolName || 'unknown_tool',
            arguments: truncateToolPayload(args, 4000),
          },
        }
      })
      out.push({ role: 'assistant', content: '', tool_calls: toolCalls })
      for (let b = 0; b < batch.length; b++) {
        const t = batch[b]
        out.push({
          role: 'tool',
          tool_call_id: toolCalls[b].id,
          name: t.toolName || 'unknown_tool',
          content: truncateToolPayload(
            resolveProposedEditResult(messages, batchStart, t.toolResult ?? t.content ?? {}),
          ),
        })
      }
      continue
    }

    if (msg.role === 'user') {
      const mapped = emitUserAssistantOnly(msg, { sendImageBytes: i === lastUserIdx })
      if (mapped) out.push(mapped)
      i += 1
      continue
    }

    if (msg.role === 'assistant') {
      // Peek following tools inside the tool zone
      let j = i + 1
      const batch: Message[] = []
      if (i >= zoneStart || j >= zoneStart) {
        while (j < messages.length && messages[j].role === 'tool') {
          if (j >= zoneStart) batch.push(messages[j])
          j += 1
        }
      }

      if (batch.length > 0) {
        const toolCalls = batch.map((t, batchIdx) => {
          const id = resolveToolCallId(t, replayId++)
          const args = resolveToolArgs(t, msg, batchIdx)
          return {
            id,
            type: 'function' as const,
            function: {
              name: t.toolName || 'unknown_tool',
              arguments: truncateToolPayload(args, 4000),
            },
          }
        })
        out.push({
          role: 'assistant',
          content: msg.content || '',
          tool_calls: toolCalls,
        })
        for (let b = 0; b < batch.length; b++) {
          const t = batch[b]
          out.push({
            role: 'tool',
            tool_call_id: toolCalls[b].id,
            name: t.toolName || 'unknown_tool',
            content: truncateToolPayload(
              resolveProposedEditResult(messages, i, t.toolResult ?? t.content ?? {}),
            ),
          })
        }
        i = j
        continue
      }

      // Assistant with persisted toolTrace (reload) and no live tool rows
      const trace = Array.isArray(msg.metadata?.toolTrace)
        ? (msg.metadata!.toolTrace as ToolTraceEntry[])
        : null
      if (trace && trace.length > 0 && i >= zoneStart) {
        const toolCalls = trace.map((t, idx) => ({
          id: t.id || `tool_replay_${replayId++}`,
          type: 'function' as const,
          function: {
            name: t.name || 'unknown_tool',
            arguments: truncateToolPayload(t.args ?? {}, 4000),
          },
        }))
        // Emit tool round first, then prose (prose often narrates after tools).
        out.push({ role: 'assistant', content: '', tool_calls: toolCalls })
        for (let b = 0; b < trace.length; b++) {
          out.push({
            role: 'tool',
            tool_call_id: toolCalls[b].id,
            name: trace[b].name || 'unknown_tool',
            content: truncateToolPayload(resolveProposedEditResult(messages, i, trace[b].result ?? {})),
          })
        }
        if (msg.content?.trim()) {
          out.push({ role: 'assistant', content: msg.content })
        }
        i += 1
        continue
      }

      const mapped = emitUserAssistantOnly(msg)
      if (mapped) out.push(mapped)
      i += 1
      continue
    }

    i += 1
  }

  return out
}

/**
 * Shrink a tool result for metadata.toolTrace without breaking reload UI.
 * Truncated/invalid JSON must still look "finished" (`success` set) or tool cards spin forever.
 */
export function compactToolResultForTrace(result: unknown): unknown {
  const original =
    result != null && typeof result === 'object' ? (result as Record<string, unknown>) : null
  const originalSuccess =
    original && typeof original.success === 'boolean' ? original.success : undefined

  try {
    const s = truncateToolPayload(result ?? {})
    const fullLen = (() => {
      try {
        return JSON.stringify(result ?? {}).length
      } catch {
        return s.length
      }
    })()
    const wasTruncated = s.length < fullLen
    const parsed = JSON.parse(s) as Record<string, unknown>
    if (parsed && typeof parsed === 'object') {
      if (parsed.success === undefined) {
        return {
          ...parsed,
          success: originalSuccess !== false,
          truncated: true,
        }
      }
      return wasTruncated ? { ...parsed, truncated: true } : parsed
    }
  } catch {
    /* fall through */
  }

  return {
    success: originalSuccess !== false,
    truncated: true,
    preview: truncateToolPayload(result ?? {}, 2000),
    ...(original?.error != null ? { error: original.error } : {}),
  }
}

/** Build metadata.toolTrace from in-stream toolResults for assistant persist / reload. */
export function buildToolTraceMetadata(
  toolResults: Array<{ tool: string; args?: unknown; result?: unknown }>,
): { toolTrace: ToolTraceEntry[] } | undefined {
  if (!toolResults.length) return undefined
  return {
    toolTrace: toolResults.map((tr, i) => ({
      id: `tool_replay_${i}`,
      name: tr.tool,
      args: tr.args,
      result: compactToolResultForTrace(tr.result),
    })),
  }
}

/** Normalize hydrated toolTrace results so missing `success` never looks "still running". */
export function ensureTerminalToolResult(result: unknown): unknown {
  if (result == null) {
    return { success: true, truncated: true, preview: '(no tool result stored)' }
  }
  if (typeof result !== 'object') {
    return { success: true, truncated: true, preview: String(result) }
  }
  const tr = result as Record<string, unknown>
  if (tr.success === true || tr.success === false) return result
  if (tr.error != null) return { ...tr, success: false }
  return { ...tr, success: true, truncated: true }
}

/** Expand saved toolTrace into in-memory tool Messages (before the assistant row). */
export function toolMessagesFromTrace(trace: unknown): Message[] {
  if (!Array.isArray(trace) || trace.length === 0) return []
  return trace.map((t: ToolTraceEntry, i) => ({
    role: 'tool' as const,
    content: '',
    toolName: t.name || 'unknown_tool',
    toolCallId: t.id || `tool_replay_${i}`,
    toolArgs: t.args,
    toolResult: ensureTerminalToolResult(t.result),
  }))
}
